import type { SignedSurfaceKind } from '@/lib/cutFillConvention';
import * as THREE from 'three';

/**
 * Stop lists for the named colormaps, read by `sampleColormap` (LandXML/vector
 * surface shading) and `gradientCss` (`DeviationColorbar`). Stops are evenly
 * spaced — index `i` sits at `i / (n - 1)`.
 *
 * **Scope of the "one table" claim.** For the seven LEGACY names
 * (SPECTRAL, RAINBOW, VIRIDIS, PLASMA, INFERNO, GRAYSCALE, YELLOW_GREEN) these
 * are APPROXIMATIONS of the @pnext/three-loader gradients: `Pointcloud.tsx`
 * still carries its own, differently-sampled Potree lists for those (11 stops
 * for SPECTRAL against the 9 here, 7 for RAINBOW, 2 for GRAYSCALE, …), and the
 * VIEWER remains the source of truth for what a deviation cloud looks like.
 * Migrating them here would change that appearance, so they stay as they are.
 *
 * `CLEARANCE` is the exception and is deliberately not like that: it is
 * defined ONLY here, and `Pointcloud.tsx` builds its Potree gradient with
 * `gradientStops('CLEARANCE')`. For that one name the cloud and the colorbar
 * cannot drift, which is what lets #f2b705 mean "profile boundary" in both.
 */
export const STOPS: Record<string, string[]> = {
  SPECTRAL: ['#3288bd', '#66c2a5', '#abdda4', '#e6f598', '#ffffbf', '#fee08b', '#fdae61', '#f46d43', '#d53e4f'],
  RAINBOW: ['#00007f', '#0000ff', '#007fff', '#00ffff', '#7fff7f', '#ffff00', '#ff7f00', '#ff0000', '#7f0000'],
  VIRIDIS: ['#440154', '#3b528b', '#21918c', '#5ec962', '#fde725'],
  PLASMA: ['#0d0887', '#6a00a8', '#b12a90', '#e16462', '#fca636', '#f0f921'],
  INFERNO: ['#000004', '#420a68', '#932667', '#dd513a', '#fca50a', '#fcffa4'],
  GRAYSCALE: ['#000000', '#888888', '#ffffff'],
  YELLOW_GREEN: ['#ffffe5', '#f7fcb9', '#addd8e', '#41ab5d', '#006837'],
  // Purpose-built for the `Clearance` scalar, which is drawn over a SYMMETRIC
  // range (`[-rampSpanM, +rampSpanM]`), so t = 0.5 is exactly the profile
  // boundary. 9 evenly-spaced stops; index 4 of 9 is exactly 0.5.
  //
  // 0.000 – 0.125 : flat neutral, the same colour twice. Deliberately FLAT:
  //                 how far outside the envelope a point sits is not a fact
  //                 anyone acts on, and a gradient there spends contrast on
  //                 the 95 % of the cloud that is background terrain — which
  //                 is how SPECTRAL turned a whole site into one loud blue
  //                 mass.
  // 0.125 – 0.375 : still cool and low-contrast; #a9adaa (0.25) is a barely
  //                 perceptible shift, #e8d9a2 (0.375) is already pale amber.
  // 0.375 – 0.500 : pale amber → amber. The approach, where the eye should
  //                 start to notice.
  // 0.500         : #f2b705 amber — the PROFILE BOUNDARY, at the ramp's exact
  //                 midpoint. The most-attended colour sits on the decision
  //                 line, and it only sits there because the range is
  //                 symmetric (see `ClearanceRunParams.rampSpanM`).
  // 0.500 – 1.000 : amber → red → dark red. Saturation AND darkness rise with
  //                 intrusion depth, so the ranking survives greyscale.
  //
  // These zones are in `t`, i.e. RELATIVE TO THE RUN'S SPAN — they are not
  // absolute clearances. `t` maps to a margin of `(0.5 - t) * 2 * rampSpanM`,
  // so on a floored run (S = 0.35 m) the warm approach starts ~0.09 m outside
  // the envelope, while on a run with a 5.9 m span everything within ~1.5 m of
  // it already reads warm. Only zero is absolute. That is the price of
  // "amber is always the boundary", and it is why the colorbar labels its
  // near-miss ticks with real numbers instead of relying on the colour.
  CLEARANCE: ['#9aa3ab', '#9aa3ab', '#a9adaa', '#e8d9a2', '#f2b705', '#ef7215', '#d92b04', '#8e1204', '#5c0a03'],
  // The band-less shape of `proudRecessedStops` below (neutral at mid-bar),
  // for a caller with no range or tolerance to hand. The cloud and the colour
  // bar of a real run use the banded function instead.
  PROUD_RECESSED: ['#1d4ed8', '#3b82f6', '#93c5fd', '#d4d4d4', '#fca5a5', '#ef4444', '#b91c1c'],
};

/**
 * The proud / recessed ramp of a Horizontal (wall) deviation — `PROUD_RECESSED`
 * (`graphql/layer.gql`). Blue recessed, a FLAT neutral grey across ±tolerance
 * and red proud, with hard steps at ±tolerance: the wall check's band inks
 * (`WALL_BANDS`), and the same blue / neutral / red the deviation histogram
 * draws. Before it (owner's run, 2026-10-25) a wall drew in SPECTRAL, so a
 * mostly-proud face was one yellow-to-red smear beside a blue/red histogram.
 *
 * Unlike every table above it cannot be a fixed list: the grey band is in
 * METRES, so its place on the ramp depends on the run's range. Hence a
 * function of `(min, max, toleranceM)`, read by the cloud (`Pointcloud.tsx`)
 * and the colour bar (`DeviationColorbar`) alike, so the two cannot drift.
 *
 * Keep in step with `proud_recessed_stops` in `scripts/render.py` (the PDF's
 * renders draw the same stops).
 */
export const PROUD_RECESSED = 'PROUD_RECESSED';
const PR_INKS = {
  recess3: '#1d4ed8',
  recess2: '#3b82f6',
  recess1: '#93c5fd',
  in: '#d4d4d4',
  proud1: '#fca5a5',
  proud2: '#ef4444',
  proud3: '#b91c1c',
} as const;

/** `[t, hex]` over `[min, max]`, `t` ascending; a repeated `t` is a hard step. */
export function proudRecessedStops(min: number, max: number, toleranceM: number): [number, string][] {
  if (!(max > min))
    return [
      [0, PR_INKS.in],
      [1, PR_INKS.in],
    ];
  const t = (v: number) => Math.min(1, Math.max(0, (v - min) / (max - min)));
  const b = Math.abs(Number.isFinite(toleranceM) ? toleranceM : 0);
  if (b > 0 && min < -b && b < max) {
    return [
      [0, PR_INKS.recess3],
      [t((min - b) / 2), PR_INKS.recess2],
      [t(-b), PR_INKS.recess1],
      [t(-b), PR_INKS.in],
      [t(b), PR_INKS.in],
      [t(b), PR_INKS.proud1],
      [t((max + b) / 2), PR_INKS.proud2],
      [1, PR_INKS.proud3],
    ];
  }
  // No band that fits inside the range: plain blue → grey → red, neutral on zero.
  const z = min < 0 && max > 0 ? t(0) : 0.5;
  return [
    [0, PR_INKS.recess3],
    [z, PR_INKS.in],
    [1, PR_INKS.proud3],
  ];
}

/** `proudRecessedStops` as a Potree gradient (`material.gradient`). */
/** Twin of server processDeviation/renderRamp.ts DEFAULT_VOLUME_TOLERANCE_M:
 *  the grey band of layers made before the effective tolerance was stored. */
export const DEFAULT_VOLUME_TOLERANCE_M = 0.01;

export function proudRecessedGradient(min: number, max: number, toleranceM: number): [number, THREE.Color][] {
  return proudRecessedStops(min, max, toleranceM).map(([p, hex]) => [p, new THREE.Color(hex)]);
}

/** `proudRecessedStops` as a CSS `linear-gradient` running min → max, left to right. */
export function proudRecessedCss(min: number, max: number, toleranceM: number): string {
  const stops = proudRecessedStops(min, max, toleranceM).map(([p, hex]) => `${hex} ${(p * 100).toFixed(2)}%`);
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

/**
 * The gradient names a layer-settings picker offers, in display order, with
 * the `layerSettings.*` key each one is labelled by.
 *
 * One list, because there were four copies of it — the point-cloud picker,
 * the LandXML surface picker, the vector-surface picker and the
 * surface-difference picker — and they had already drifted (`CLEARANCE`
 * exists in exactly one of them, correctly, but by accident of editing rather
 * than by intent). Anything a picker offers that the others must not is
 * appended at the call site.
 *
 * Every `value` here is a `PointColorType` member (`graphql/layer.gql`),
 * because that is the column these pickers write. A palette carries NO
 * orientation: which end is cut is `@/lib/cutFillConvention`'s
 * `rampPosition`, per kind of run.
 */
export const VIEWER_GRADIENT_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'GRAYSCALE', labelKey: 'layerSettings.grayscale' },
  { value: 'RAINBOW', labelKey: 'layerSettings.rainbow' },
  { value: 'SPECTRAL', labelKey: 'layerSettings.spectral' },
  { value: 'PLASMA', labelKey: 'layerSettings.plasma' },
  { value: 'YELLOW_GREEN', labelKey: 'layerSettings.yellowGreen' },
  { value: 'VIRIDIS', labelKey: 'layerSettings.viridis' },
  { value: 'INFERNO', labelKey: 'layerSettings.inferno' },
];

/**
 * The MATPLOTLIB ramp names the run dialogs and the report dialog offer — a
 * different namespace from `STOPS` above: these travel to the Python workers
 * (`render.py`, `render_per_surface.py`, `surface_difference.py`,
 * `deviation.py`) and are looked up in `matplotlib.colormaps`, so they must be
 * spelled matplotlib's way.
 *
 * ONE list, two orientations. A diverging ramp has a red end and a blue end,
 * and which SIGN that red end sits on is the whole cut/fill convention: a
 * deviation run is cut at +, a surface-difference run is cut at −
 * (`@/lib/cutFillConvention`). So each entry names the ramp once and gives the
 * matplotlib spelling that puts red on the cut side for each kind. Offering
 * the raw `Spectral` / `Spectral_r` pair to the user instead would be offering
 * them the chance to break the convention with no way to tell which one is
 * right.
 *
 * `jet` is deliberately absent. It is a rainbow over a signed quantity: it
 * invents an ordering the data does not have and puts its most salient band at
 * an arbitrary value — the objection `bandGeometry.ts` already records against
 * `RAINBOW`, and the reason the convention picks a diverging ramp at all.
 * Existing runs that recorded `jet` still render; it is simply not offered.
 *
 * `viridis` is SEQUENTIAL, so unlike a diverging ramp it has only one strong
 * end, not two — reversing it is the only way to move that end. The bake
 * (`scripts/surface_difference.py::_dz_to_rgba`) paints low dz — the CUT side
 * of a surface difference — at `cmap(0)`, which is viridis's WEAK (dark
 * purple) end, not its strong (yellow) one. So the two kinds do NOT share a
 * spelling here: `deviation` keeps plain `viridis` (positive/cut already maps
 * to the ramp's high, strong end), while `surfaceDifference` takes `viridis_r`
 * so the bake's low end (cut) lands on the strong end instead — the same rule
 * a diverging row applies by choosing which red end faces low.
 */
export const MATPLOTLIB_RAMP_OPTIONS: { label: string; deviation: string; surfaceDifference: string }[] = [
  { label: 'Spectral', deviation: 'Spectral_r', surfaceDifference: 'Spectral' },
  { label: 'RdBu', deviation: 'RdBu_r', surfaceDifference: 'RdBu' },
  { label: 'coolwarm', deviation: 'coolwarm', surfaceDifference: 'coolwarm_r' },
  { label: 'viridis', deviation: 'viridis', surfaceDifference: 'viridis_r' },
];

/** The ramp picker's options for one kind of run, cut-side-red throughout. */
export function matplotlibRampOptions(kind: SignedSurfaceKind): { value: string; label: string }[] {
  return MATPLOTLIB_RAMP_OPTIONS.map((o) => ({ value: o[kind], label: o.label }));
}

/**
 * `[position, color]` pairs for `@pnext/three-loader`'s `material.gradient`.
 * Used by `Pointcloud.tsx` for `CLEARANCE` only (the seven legacy names keep
 * their hand-written Potree lists there — see `STOPS`), so for that gradient
 * the Potree material and the CSS colorbar are the same colours by
 * construction rather than by transcription.
 */
export function gradientStops(name: string): [number, THREE.Color][] {
  const stops = STOPS[name] ?? STOPS.SPECTRAL;
  return stops.map((hex, i) => [i / (stops.length - 1), new THREE.Color(hex)]);
}

/**
 * CSS `linear-gradient(...)` for a named colormap, from the same `STOPS`.
 * Approximate for the seven legacy names (the viewer is the source of truth
 * for those); exact for `CLEARANCE`.
 */
export function gradientCss(name: string, reversed = false): string {
  // `reversed` draws the SAME palette backwards, which is how a bar whose axis
  // runs min -> max left to right still puts the cut end's colour on the cut
  // end (`@/lib/cutFillConvention`: the palette's high end is cut, and for a
  // surface difference the cut end is `min`). Reversing the CSS direction
  // rather than the stop list keeps one array per palette.
  return `linear-gradient(to ${reversed ? 'left' : 'right'}, ${(STOPS[name] ?? STOPS.SPECTRAL).join(', ')})`;
}

/** Sample a named colormap at t ∈ [0,1] and return a THREE.Color. */
export function sampleColormap(name: string, t: number, out: THREE.Color = new THREE.Color()): THREE.Color {
  const stops = STOPS[name] ?? STOPS.SPECTRAL;
  const x = Math.max(0, Math.min(1, t)) * (stops.length - 1);
  const i = Math.floor(x);
  const f = x - i;
  out.set(stops[i]);
  if (i < stops.length - 1) {
    const b = new THREE.Color(stops[i + 1]);
    out.lerp(b, f);
  }
  return out;
}
